/* eslint-disable react-hooks/exhaustive-deps */
import { Outlet, useLocation } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import { useDispatch, useSelector } from 'react-redux';
import { saveAll } from '../store/items';
import { removeChild, upsertChild } from '../store/navigation';
import {
  setIsMobile,
  setIsTouch,
  setIsLandscape,
  setSidebarSize,
  setDrawerOpen,
  setDrawerSize,
  setNavigationSize,
  setTheme,
  setOpenModales,
} from '../store/global';
import { useWindowSize } from '../hooks/useWindowSize';
import { Drawer } from '@mui/material';
import { ItemShort } from '../components/item/item.Short';
import { ItemLarge } from '../components/item/item.Large';
import { themesBase } from '../content/themes';
import { NavigationPassword } from '../components/navigation/navigation.Passwords';
import { canOpenItem, hasResolvedPermissionScope } from '../utils/permissions';
import { ensureUserStorageHydrated, getStorageValue } from '../utils/userStorage';

const Layout = () => {
  const dispatch = useDispatch();
  const windowSize = useWindowSize();
  const location = useLocation();

  const drawer = useSelector(state => state.global.drawer);
  const theme = useSelector(state => state.global.theme);
  const sidebar = useSelector(state => state.global.sidebar);
  const navigation = useSelector(state => state.global.navigation);
  const isMobile = useSelector(state => state.global.isMobile);
  const items = useSelector(state => state.items);
  const itemsRef = useRef(items);
  const isResizingDrawerRef = useRef(false);
  const drawerSizeRef = useRef(drawer.size);
  const lastDesktopDrawerSizeRef = useRef(drawer.size);
  const lastDesktopNavigationSizeRef = useRef(navigation.size);
  const wasMobileRef = useRef(isMobile);
  const pendingDrawerSizeRef = useRef(null);
  const drawerResizeRafRef = useRef(null);
  const [isResizingDrawer, setIsResizingDrawer] = useState(false);
  const [drawerSidebarOpen, setDrawerSidebarOpen] = useState(true);
  const MIN_DRAWER_SIZE = 400;

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    drawerSizeRef.current = drawer.size;
  }, [drawer.size]);

  useEffect(() => {
    const wasMobile = wasMobileRef.current;
    wasMobileRef.current = isMobile;

    if (!isMobile && wasMobile) {
      dispatch(setDrawerSize(lastDesktopDrawerSizeRef.current));
      dispatch(setNavigationSize(lastDesktopNavigationSizeRef.current));
      return;
    }

    if (!isMobile) {
      lastDesktopDrawerSizeRef.current = drawer.size;
      lastDesktopNavigationSizeRef.current = navigation.size;
    }
  }, [dispatch, drawer.size, isMobile, navigation.size]);

  useEffect(() => {
    if (drawer.open) {
      setDrawerSidebarOpen(true);
    }
  }, [drawer.open]);

  const getParentIds = item => {
    if (!item) {
      return [];
    }
    const candidates = [item.campaign, item.world, item.universe];
    return candidates.filter(
      id =>
        id &&
        id !== 'non défini' &&
        id !== process.env.REACT_APP_NONE_VISITOR
    );
  };

  useEffect(() => {
    ensureUserStorageHydrated();
  }, []);

  useEffect(() => {
    // save themes base in local storage
    for (const themeName in themesBase) {
      window.localStorage.setItem(
        `theme-${themeName}`,
        JSON.stringify(themesBase[themeName])
      );
    }

    const currentTheme = getStorageValue('currentTheme');
    if (currentTheme) {
      dispatch(setTheme(JSON.parse(window.localStorage.getItem(currentTheme))));
    }

    // INIT SOCKET
    // With REACT_APP_API_URL="/api" (dev proxy), Socket.IO must use `/api/socket.io` as path.
    // Passing "/api" directly to `io()` creates a namespace, not the transport endpoint.
    const rawApiUrl = process.env.REACT_APP_API_URL || '';
    const disableSocket = process.env.REACT_APP_DISABLE_SOCKET === 'true';
    const allowPolling = process.env.REACT_APP_SOCKET_ALLOW_POLLING === 'true';
    if (disableSocket) {
      return;
    }

    let socketBaseUrl = undefined;
    let socketPath = '/socket.io';
    if (rawApiUrl) {
      if (rawApiUrl.startsWith('/')) {
        const prefix = rawApiUrl.replace(/\/$/, '');
        socketPath = `${prefix}/socket.io`;
      } else {
        try {
          const parsed = new URL(rawApiUrl);
          socketBaseUrl = `${parsed.protocol}//${parsed.host}`;
          const prefix =
            parsed.pathname && parsed.pathname !== '/'
              ? parsed.pathname.replace(/\/$/, '')
              : '';
          socketPath = `${prefix}/socket.io`;
        } catch (error) {
          socketBaseUrl = rawApiUrl;
          socketPath = '/socket.io';
        }
      }
    }

    const socket = io(socketBaseUrl, {
      path: socketPath,
      // Use websocket by default to avoid polling storms on failing local setups.
      transports: allowPolling ? ['websocket', 'polling'] : ['websocket'],
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });
    socket?.on('items', element => {
      const prev = itemsRef.current?.[element?._id];
      let merged = element;
      if (prev?.__optimisticAt && typeof prev.__optimisticAt === 'object') {
        const OPTIMISTIC_TTL = 3000;
        const now = Date.now();
        const keptOptimistic = {};
        Object.entries(prev.__optimisticAt).forEach(([field, ts]) => {
          if (element && field in element && element[field] === prev[field]) {
            return;
          }
          if (now - ts < OPTIMISTIC_TTL) {
            keptOptimistic[field] = ts;
          }
        });
        if (Object.keys(keptOptimistic).length > 0) {
          merged = { ...element };
          Object.keys(keptOptimistic).forEach(field => {
            if (field in prev) {
              merged[field] = prev[field];
            }
          });
          merged.__optimisticAt = keptOptimistic;
        }
      }
      dispatch(saveAll({ item: merged }));
      const prevParents = getParentIds(prev);
      const nextParents = getParentIds(element);
      prevParents.forEach(parentId => {
        if (!nextParents.includes(parentId)) {
          dispatch(removeChild({ parentId, childId: element._id }));
        }
      });
      nextParents.forEach(parentId => {
        dispatch(upsertChild({ parentId, childId: element._id }));
      });
    });
    return () => {
      socket.off('items');
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    // "isMobile" used to be based on orientation (width < height). That creates a lot of false positives
    // (desktop windowed, tablets in landscape, split view, drawer resize, etc.).
    // We now treat it as a "compact layout" flag driven by width.
    const COMPACT_WIDTH = 900;
    const nextIsMobile = Number(windowSize?.width) <= COMPACT_WIDTH;
    dispatch(setIsMobile(nextIsMobile));
  }, [windowSize?.width, dispatch]);

  useEffect(() => {
    // Sidebar sizing should follow the compact layout mode, not orientation.
    if (isMobile) {
      dispatch(setSidebarSize(windowSize.width));
    } else {
      dispatch(setSidebarSize(400));
    }
  }, [isMobile, windowSize.width, dispatch]);

  useEffect(() => {
    // Input capabilities (touch vs mouse) and orientation are separate concerns.
    if (typeof window !== 'object' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mqlTouch = window.matchMedia('(pointer: coarse)');
    const mqlLandscape = window.matchMedia('(orientation: landscape)');

    const computeTouch = () =>
      Boolean(mqlTouch.matches) || (navigator?.maxTouchPoints ?? 0) > 0;
    const computeLandscape = () => Boolean(mqlLandscape.matches);

    const updateFlags = () => {
      dispatch(setIsTouch(computeTouch()));
      dispatch(setIsLandscape(computeLandscape()));
    };

    updateFlags();
    const onChange = () => updateFlags();
    // Safari < 14 uses addListener/removeListener
    if (typeof mqlTouch.addEventListener === 'function') {
      mqlTouch.addEventListener('change', onChange);
      mqlLandscape.addEventListener('change', onChange);
      return () => {
        mqlTouch.removeEventListener('change', onChange);
        mqlLandscape.removeEventListener('change', onChange);
      };
    }
    mqlTouch.addListener(onChange);
    mqlLandscape.addListener(onChange);
    return () => {
      mqlTouch.removeListener(onChange);
      mqlLandscape.removeListener(onChange);
    };
  }, [dispatch]);

  useEffect(() => {
    if (location?.pathname === '/' && drawer.open) {
      dispatch(setDrawerOpen(null));
    }
  }, [location?.pathname, drawer.open, dispatch]);

  useEffect(() => {
    if (!drawer.open) {
      return;
    }
    const item = items?.[drawer.open];
    if (!item) {
      return;
    }
    const campaign = items?.[item?.campaign];
    const world = items?.[item?.world];
    const universe = items?.[item?.universe];
    if (!hasResolvedPermissionScope(item, campaign, world, universe)) {
      dispatch(setOpenModales({ id: 'passwords', open: false }));
      dispatch(setDrawerOpen(null));
      return;
    }
    if (canOpenItem(item, campaign, world, universe, items)) {
      dispatch(setOpenModales({ id: 'passwords', open: false }));
      return;
    }
    dispatch(setOpenModales({ id: 'passwords', open: false }));
    dispatch(setDrawerOpen(null));
  }, [drawer.open, items, dispatch]);

  useEffect(() => {
    const handleMouseMove = event => {
      if (!isResizingDrawerRef.current || isMobile) {
        return;
      }
      const marginRight = navigation.open && !isMobile ? navigation.size : 0;
      const maxSize = Math.max(
        MIN_DRAWER_SIZE,
        windowSize.width - marginRight - 40
      );
      const nextSize = Math.min(
        maxSize,
        Math.max(
          MIN_DRAWER_SIZE,
          windowSize.width - marginRight - event.clientX
        )
      );
      pendingDrawerSizeRef.current = nextSize;
      if (drawerResizeRafRef.current !== null) {
        return;
      }
      drawerResizeRafRef.current = window.requestAnimationFrame(() => {
        drawerResizeRafRef.current = null;
        const pendingSize = pendingDrawerSizeRef.current;
        if (
          typeof pendingSize === 'number' &&
          pendingSize !== drawerSizeRef.current
        ) {
          drawerSizeRef.current = pendingSize;
          dispatch(setDrawerSize(pendingSize));
        }
      });
    };

    const handleMouseUp = () => {
      if (!isResizingDrawerRef.current) {
        return;
      }
      isResizingDrawerRef.current = false;
      setIsResizingDrawer(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (drawerResizeRafRef.current !== null) {
        window.cancelAnimationFrame(drawerResizeRafRef.current);
        drawerResizeRafRef.current = null;
      }
    };
  }, [dispatch, isMobile, navigation.open, navigation.size, windowSize.width]);

  const handleDrawerResizeStart = event => {
    if (isMobile || event.button !== 0) {
      return;
    }
    event.preventDefault();
    isResizingDrawerRef.current = true;
    setIsResizingDrawer(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const setThemeToCSSvar = () => {
    const varKeys = Object.keys(theme);
    let str = ':root {';
    for (const key of varKeys) {
      str += `--${key}: ${theme[key]};`;
    }
    str += '}';
    return str;
  };
  const isDrawerWide = drawer.size > windowSize.height;
  const drawerRightWidth =
    drawer.size - (sidebar.open ? sidebar.size : 0);
  const drawerItem = drawer.open ? items?.[drawer.open] : null;
  const drawerCampaign = drawerItem ? items?.[drawerItem?.campaign] : null;
  const drawerWorld = drawerItem ? items?.[drawerItem?.world] : null;
  const drawerUniverse = drawerItem ? items?.[drawerItem?.universe] : null;
  const canRenderDrawerContent =
    !drawerItem ||
    canOpenItem(drawerItem, drawerCampaign, drawerWorld, drawerUniverse, items);

  return (
    <div className={`h-full`}>
      <style type='text/css'>{setThemeToCSSvar()}</style>
      <Drawer
        disableEnforceFocus
        anchor={'right'}
        open={typeof drawer.open === 'string'}
        onClose={() => dispatch(setDrawerOpen(null))}
        PaperProps={{
          className: 'bg-main text-contrast',
          style: {
            marginRight: navigation.open && !isMobile ? navigation.size : 0,
            width: drawer.size,
            minWidth: MIN_DRAWER_SIZE,
          },
        }}
      >
        {drawer.open && <NavigationPassword itemId={drawer.open} />}
        {!isMobile && (
          <div
            className='absolute left-0 top-0 h-full w-2 cursor-col-resize z-50 pointer-events-none'
            onMouseDown={handleDrawerResizeStart}
          >
            <div className='h-full w-px2 bg-contrast bg-opacity-10 ml-px3 pointer-events-auto' />
          </div>
        )}
        {canRenderDrawerContent && isDrawerWide ? (
          <div className='flex h-full bg-main text-contrast'>
            <ItemShort
              itemId={drawer.open}
              displayType={'sidebar'}
              disableTransitions={isResizingDrawer}
              sidebarOpenOverride={drawerSidebarOpen}
              onSidebarClose={() => setDrawerSidebarOpen(false)}
            />
            <ItemLarge
              itemId={drawer.open}
              containerWidth={drawerRightWidth}
              hideNavigation={true}
              ignoreSidebar={true}
              useMobileTabState={true}
              sidebarOpenOverride={drawerSidebarOpen}
              onSidebarOpen={() => setDrawerSidebarOpen(true)}
            />
          </div>
        ) : canRenderDrawerContent ? (
          <ItemShort
            itemId={drawer.open}
            displayType={'drawer'}
            disableTransitions={isResizingDrawer}
            drawerIsWide={isDrawerWide}
          />
        ) : null}
      </Drawer>
      <Outlet />
    </div>
  );
};

export default Layout;
